import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    space, fontWeights, colors, fontSizes
} from 'style/config';
import {
    Box, Text, Button, Link, Grid
} from 'components/ui';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import { getImageAltText } from 'utils/Accessibility';
import BasketUtils from 'utils/Basket';
import UrlUtils from 'utils/Url';
import DeliveryFrequencyUtils from 'utils/DeliveryFrequency';
import ManageSubscriptionModalEN from 'components/GlobalModals/ManageSubscriptionModal/locales/ManageSubscriptionModal_en_US';
import ManageSubscriptionModalFR from 'components/GlobalModals/ManageSubscriptionModal/locales/ManageSubscriptionModal_fr_CA';
import localeUtils from 'utils/LanguageLocale';

const { formatCurrency, formatSavingAmountString, formatFrequencyType } = DeliveryFrequencyUtils;
const { getLocalisedDataFromResource } = localeUtils;

const IMAGE_SIZE = 64;

const parsePerUnitPrice = price => {
    if (typeof price !== 'string' || !/\d/.test(price)) {
        return NaN;
    }

    return parseFloat(BasketUtils.removeCurrency(price));
};

/**
 * Confirmation view for an Auto-Replenish quantity change: shows the product with the updated
 * quantity and recomputed per-line pricing, plus Cancel / Confirm actions.
 */
const ConfirmUpdateView = ({
    product, pendingQty, frequency, frequencyType, onConfirm, onCancel, isSubmitting, error, confirmRef
}) => {
    const getText = getLocalisedDataFromResource(ManageSubscriptionModalEN, ManageSubscriptionModalFR);
    const perUnit = parsePerUnitPrice(product?.price);
    const hasPrice = !Number.isNaN(perUnit);
    const hasFrequency = !!(frequency && frequencyType);
    const shouldShowItemVariation = !!(product?.variationType && product?.variationValue && product?.skuId);
    const frequencyLabel = hasFrequency
        ? `${frequency} ${formatFrequencyType(frequency, frequencyType)}`
        : null;

    const savingFor = qty => formatSavingAmountString(product, qty, false, true, false, product.acceleratedPromotion);
    const originalFor = qty => formatCurrency(perUnit, qty);

    const productRow = qty => (
        <Grid css={styles.row}>
            <ProductImage
                id={product.skuId}
                size={IMAGE_SIZE}
                skuImages={product.skuImages}
                disableLazyLoad={true}
                altText={getImageAltText(product)}
            />
            <Box>
                <Text
                    is='h4'
                    fontWeight={fontWeights.bold}
                    children={product.brandName}
                />
                <Text
                    is='p'
                    children={product.productName}
                />
                {shouldShowItemVariation && (
                    <Text
                        is='p'
                        css={styles.secondary}
                    >
                        {product.variationType}: {product.variationValue}
                    </Text>
                )}
                <Text
                    is='p'
                    css={styles.priceLine}
                >
                    {hasPrice && <Text is='span' css={styles.priceSaving} children={savingFor(qty)} />}
                    {hasPrice && ' '}
                    {hasPrice && <Text is='del' children={originalFor(qty)} />}
                    {hasPrice ? ` · ${getText('qty')} ${qty}` : `${getText('qty')} ${qty}`}
                </Text>
                {frequencyLabel && (
                    <Text is='p'>
                        {`${getText('deliverEveryLabel')} `}
                        <Text
                            is='span'
                            fontWeight={fontWeights.bold}
                            children={frequencyLabel}
                        />
                    </Text>
                )}
            </Box>
        </Grid>
    );

    return (
        <Box>
            <Box css={styles.section}>
                <Text
                    is='p'
                    fontWeight={fontWeights.bold}
                    css={styles.sectionHeading}
                    children={getText('current')}
                />
                {productRow(product.qty)}
            </Box>
            <Box css={styles.section}>
                <Text
                    is='p'
                    fontWeight={fontWeights.bold}
                    css={styles.sectionHeading}
                    children={getText('updateTo')}
                />
                {productRow(pendingQty)}
            </Box>
            <Box css={styles.divider} />
            <Text
                is='p'
                css={styles.consent}
            >
                {`${getText('recurringConsent')} `}
                <Link
                    color='blue'
                    href={UrlUtils.getLink('/beauty/auto-replenish')}
                    children={getText('aboutAutoReplenish')}
                />
            </Text>
            {error && (
                <Text
                    is='p'
                    color='error'
                    css={styles.consent}
                    children={getText('updateError')}
                />
            )}
            <Grid css={styles.footerActions}>
                <Button
                    variant='secondary'
                    onClick={onCancel}
                    children={getText('cancel')}
                />
                <Button
                    ref={confirmRef}
                    variant='primary'
                    disabled={isSubmitting}
                    onClick={onConfirm}
                    children={error ? getText('retry') : getText('confirmButton')}
                />
            </Grid>
        </Box>
    );
};

const styles = {
    section: {
        marginBottom: space[3]
    },
    sectionHeading: {
        marginBottom: space[1]
    },
    row: {
        alignItems: 'start',
        gap: space[2],
        gridTemplateColumns: 'auto 1fr'
    },
    priceLine: {
        marginTop: space[2],
        marginBottom: space[2]
    },
    divider: {
        height: '1px',
        backgroundColor: colors.lightGray,
        marginBottom: space[3]
    },
    consent: {
        marginBottom: space[2]
    },
    footerActions: {
        gap: '15px',
        gridTemplateColumns: '1fr 1fr'
    },
    priceSaving: {
        fontWeight: fontWeights.bold,
        color: colors.red
    },
    secondary: {
        color: colors.gray,
        fontSize: fontSizes.sm
    }
};

ConfirmUpdateView.propTypes = {
    product: PropTypes.shape({
        skuId: PropTypes.string,
        brandName: PropTypes.string,
        productName: PropTypes.string,
        variationType: PropTypes.string,
        variationValue: PropTypes.string,
        skuImages: PropTypes.object,
        qty: PropTypes.number,
        price: PropTypes.string,
        acceleratedPromotion: PropTypes.object
    }).isRequired,
    pendingQty: PropTypes.number.isRequired,
    frequency: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    frequencyType: PropTypes.string,
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    isSubmitting: PropTypes.bool,
    error: PropTypes.bool,
    confirmRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
};

export default wrapFunctionalComponent(ConfirmUpdateView, 'ConfirmUpdateView');
